import React, { useState, useEffect } from "react";
import "./Admin.css";
import { getToken, getUser } from "../utils/session";
import axios from "axios";
import { API_BASE } from "../utils/apiConfig";

function Admin({ onBack }) {
    const currentUser = getUser();
    const isPlatformAdmin = currentUser?.role === "admin";
    const [docs, setDocs] = useState([]);
    const [status, setStatus] = useState(null);
    const [uploadingFiles, setUploadingFiles] = useState({});
    const [isLoadingDocs, setIsLoadingDocs] = useState(true);
    const [knowledgeStatus, setKnowledgeStatus] = useState(null);
    const [users, setUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);
    const [workspaces, setWorkspaces] = useState([]);
    const [auditEvents, setAuditEvents] = useState([]);
    const [newWorkspaceName, setNewWorkspaceName] = useState("");
    const [invitation, setInvitation] = useState({
        name: "",
        email: "",
        role: "author",
        workspaceId: isPlatformAdmin ? "" : (currentUser?.workspaceId || "")
    });

    const fetchDocs = async () => {
        setIsLoadingDocs(true);
        try {
            const res = await axios.get(`${API_BASE}/admin/documents`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            setDocs(res.data || []);
        } catch (err) {
            console.error("Failed to fetch admin docs:", err);
            setStatus({ type: "error", text: "Failed to load knowledge base files." });
        } finally {
            setIsLoadingDocs(false);
        }
    };

    const fetchKnowledgeStatus = async () => {
        try {
            const res = await axios.get(`${API_BASE}/admin/knowledge-status`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            setKnowledgeStatus(res.data);
        } catch (err) {
            console.error("Failed to fetch knowledge status:", err);
            setKnowledgeStatus({ status: "unavailable", ready: false, chunk_count: 0 });
        }
    };

    const fetchUsers = async () => {
        setIsLoadingUsers(true);
        try {
            const res = await axios.get(`${API_BASE}/auth/admin/users`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            setUsers(res.data || []);
        } catch (err) {
            console.error("Failed to fetch users:", err);
            setStatus({ type: "error", text: "Failed to load user access information." });
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const fetchWorkspaces = async () => {
        if (!isPlatformAdmin) return;
        try {
            const res = await axios.get(`${API_BASE}/auth/admin/workspaces`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            setWorkspaces(res.data || []);
        } catch (err) {
            setStatus({ type: "error", text: "Failed to load client workspaces." });
        }
    };

    const fetchAuditEvents = async () => {
        try {
            const res = await axios.get(`${API_BASE}/auth/admin/audit-events`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            setAuditEvents(res.data || []);
        } catch (err) {
            setStatus({ type: "error", text: "Failed to load security audit events." });
        }
    };

    useEffect(() => {
        if (isPlatformAdmin) {
            fetchDocs();
            fetchKnowledgeStatus();
            fetchWorkspaces();
        } else {
            setIsLoadingDocs(false);
        }
        fetchUsers();
        fetchAuditEvents();
        // Initial administration snapshot; later mutations refresh their affected sections.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const createWorkspace = async (event) => {
        event.preventDefault();
        const name = newWorkspaceName.trim();
        if (!name) return;
        try {
            await axios.post(
                `${API_BASE}/auth/admin/workspaces`,
                { name },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            setNewWorkspaceName("");
            setStatus({ type: "success", text: "Client workspace created." });
            await Promise.all([fetchWorkspaces(), fetchAuditEvents()]);
        } catch (err) {
            setStatus({ type: "error", text: err?.response?.data?.error || "Failed to create workspace." });
        }
    };

    const updateUserAccess = async (userId, update) => {
        setStatus(null);
        try {
            const res = await axios.patch(
                `${API_BASE}/auth/admin/users/${userId}/access`,
                update,
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            setUsers((current) => current.map((user) =>
                user.id === userId ? res.data.user : user
            ));
            setStatus({ type: "success", text: "User access updated." });
            await fetchAuditEvents();
        } catch (err) {
            const message = err?.response?.data?.error || "Failed to update user access.";
            setStatus({ type: "error", text: message });
        }
    };

    const sendInvitation = async (event) => {
        event.preventDefault();
        const payload = {
            name: invitation.name.trim(),
            email: invitation.email.trim().toLowerCase(),
            role: invitation.role,
            workspaceId: invitation.role === "admin" ? null : invitation.workspaceId
        };
        if (!payload.name || !payload.email || (payload.role !== "admin" && !payload.workspaceId)) {
            setStatus({ type: "error", text: "Complete the invitation name, email, role and workspace." });
            return;
        }
        try {
            await axios.post(
                `${API_BASE}/auth/admin/invitations`,
                payload,
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            setInvitation({
                name: "",
                email: "",
                role: "author",
                workspaceId: isPlatformAdmin ? "" : (currentUser?.workspaceId || "")
            });
            setStatus({ type: "success", text: "Invitation sent. The link will expire in seven days." });
            await fetchAuditEvents();
        } catch (err) {
            setStatus({ type: "error", text: err?.response?.data?.error || "Failed to send invitation." });
        }
    };

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        setStatus(null);
        e.target.value = ""; // clear input

        setUploadingFiles(Object.fromEntries(files.map((file) => [file.name, 0])));
        setStatus({ type: "info", text: "Uploading files and rebuilding the knowledge base..." });

        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));

        try {
            await axios.post(`${API_BASE}/admin/upload-document`, formData, {
                headers: { Authorization: `Bearer ${getToken()}` },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = progressEvent.total
                        ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                        : 0;
                    setUploadingFiles(Object.fromEntries(
                        files.map((file) => [file.name, percentCompleted])
                    ));
                },
            });
            setStatus({
                type: "success",
                text: `${files.length} file${files.length === 1 ? "" : "s"} uploaded and indexed successfully.`
            });
        } catch (err) {
            console.error("Upload error:", err);
            const message = err?.response?.data?.error || "Knowledge-base upload failed.";
            setStatus({ type: "error", text: message });
        } finally {
            setUploadingFiles({});
            await Promise.all([fetchDocs(), fetchKnowledgeStatus()]);
        }
    };

    const handleDelete = async (filename) => {
        setStatus({ type: "info", text: `Deleting ${filename} and rebuilding index...` });
        try {
            await axios.delete(`${API_BASE}/admin/documents/${filename}`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            setStatus({ type: "success", text: `${filename} deleted and knowledge base updated successfully.` });
            fetchDocs();
            fetchKnowledgeStatus();
        } catch (err) {
            console.error("Delete error:", err);
            setStatus({ type: "error", text: `Failed to delete ${filename}.` });
        }
    };

    // Pending file keys
    const uploadingKeys = Object.keys(uploadingFiles);

    return (
        <div className="admin-container">
            <div className="admin-card">
                <button className="back-btn" onClick={onBack}>← Back to Chat</button>
                <div>
                    <h2>LogicLine Administration</h2>
                    <p className="subtitle">Manage client workspaces, user access and security evidence</p>
                </div>

                {isPlatformAdmin && (
                    <section className="admin-section" aria-labelledby="workspace-heading">
                        <div className="admin-section-heading">
                            <div>
                                <h3 id="workspace-heading">Client workspaces</h3>
                                <p>Separate each client’s users and outputs.</p>
                            </div>
                        </div>
                        <form className="workspace-form" onSubmit={createWorkspace}>
                            <input
                                aria-label="New workspace name"
                                value={newWorkspaceName}
                                onChange={(event) => setNewWorkspaceName(event.target.value)}
                                placeholder="Client organisation name"
                                maxLength={100}
                            />
                            <button type="submit" className="access-button" disabled={!newWorkspaceName.trim()}>
                                Create workspace
                            </button>
                        </form>
                    </section>
                )}

                <section className="admin-section" aria-labelledby="invite-heading">
                    <div className="admin-section-heading">
                        <div>
                            <h3 id="invite-heading">Invite a user</h3>
                            <p>Send a seven-day, single-use invitation into a controlled client workspace.</p>
                        </div>
                    </div>
                    <form className="invitation-form" onSubmit={sendInvitation}>
                        <label>
                            <span>Name</span>
                            <input
                                aria-label="Invitee name"
                                value={invitation.name}
                                onChange={(event) => setInvitation((current) => ({ ...current, name: event.target.value }))}
                                maxLength={100}
                            />
                        </label>
                        <label>
                            <span>Email</span>
                            <input
                                aria-label="Invitee email"
                                type="email"
                                value={invitation.email}
                                onChange={(event) => setInvitation((current) => ({ ...current, email: event.target.value }))}
                            />
                        </label>
                        <label>
                            <span>Role</span>
                            <select
                                aria-label="Invitee role"
                                value={invitation.role}
                                onChange={(event) => setInvitation((current) => ({ ...current, role: event.target.value }))}
                            >
                                <option value="author">Author</option>
                                <option value="reviewer">Reviewer</option>
                                <option value="approver">Approver</option>
                                <option value="client_admin">Client administrator</option>
                                {isPlatformAdmin && <option value="admin">Platform administrator</option>}
                            </select>
                        </label>
                        {isPlatformAdmin ? (
                            <label>
                                <span>Workspace</span>
                                <select
                                    aria-label="Invitee workspace"
                                    value={invitation.workspaceId}
                                    disabled={invitation.role === "admin"}
                                    onChange={(event) => setInvitation((current) => ({ ...current, workspaceId: event.target.value }))}
                                >
                                    <option value="">Select workspace</option>
                                    {workspaces.filter((workspace) => workspace.active).map((workspace) => (
                                        <option value={workspace.id} key={workspace.id}>{workspace.name}</option>
                                    ))}
                                </select>
                            </label>
                        ) : (
                            <input type="hidden" value={currentUser?.workspaceId || ""} readOnly />
                        )}
                        <button type="submit" className="access-button">Send invitation</button>
                    </form>
                </section>

                <section className="admin-section" aria-labelledby="user-access-heading">
                    <div className="admin-section-heading">
                        <div>
                            <h3 id="user-access-heading">User access</h3>
                            <p>Roles are enforced by the backend. Deactivation takes effect on the next request.</p>
                        </div>
                    </div>

                    {isLoadingUsers ? (
                        <div className="admin-spinner" aria-label="Loading users"></div>
                    ) : users.length === 0 ? (
                        <p className="admin-empty">No users found.</p>
                    ) : (
                        <div className="admin-user-list">
                            {users.map((user) => {
                                const isCurrentUser = user.id === getUser()?.id;
                                return (
                                    <div className="admin-user-row" key={user.id}>
                                        <div className="admin-user-identity">
                                            <strong>{user.name || "Unnamed user"}</strong>
                                            <span>{user.email}</span>
                                        </div>
                                        <span className={`access-state ${user.active ? "active" : "inactive"}`}>
                                            {user.active ? "Active" : "Inactive"}
                                        </span>
                                        <label className="role-control">
                                            <span>Role</span>
                                            <select
                                                aria-label={`Role for ${user.name || user.email}`}
                                                value={user.role}
                                                disabled={isCurrentUser}
                                                onChange={(event) => updateUserAccess(user.id, { role: event.target.value })}
                                            >
                                                <option value="author">Author</option>
                                                <option value="reviewer">Reviewer</option>
                                                <option value="approver">Approver</option>
                                                <option value="client_admin">Client administrator</option>
                                                {isPlatformAdmin && <option value="admin">Platform administrator</option>}
                                            </select>
                                        </label>
                                        {isPlatformAdmin && (
                                            <label className="role-control">
                                                <span>Workspace</span>
                                                <select
                                                    aria-label={`Workspace for ${user.name || user.email}`}
                                                    value={user.workspaceId || ""}
                                                    disabled={isCurrentUser}
                                                    onChange={(event) => updateUserAccess(user.id, { workspaceId: event.target.value || null })}
                                                >
                                                    <option value="">Unassigned</option>
                                                    {workspaces.filter((workspace) => workspace.active).map((workspace) => (
                                                        <option value={workspace.id} key={workspace.id}>{workspace.name}</option>
                                                    ))}
                                                </select>
                                            </label>
                                        )}
                                        <button
                                            type="button"
                                            className={user.active ? "access-button danger" : "access-button"}
                                            disabled={isCurrentUser}
                                            aria-label={`${user.active ? "Deactivate" : "Activate"} ${user.name || user.email}`}
                                            onClick={() => updateUserAccess(user.id, { active: !user.active })}
                                        >
                                            {user.active ? "Deactivate" : "Activate"}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {isPlatformAdmin && <section className="admin-section" aria-labelledby="knowledge-heading">
                    <div className="admin-section-heading">
                        <div>
                            <h3 id="knowledge-heading">Regulatory knowledge</h3>
                            <p>Manage PDF, DOCX and TXT sources used by controlled analyses.</p>
                        </div>
                    </div>

                {knowledgeStatus && (
                    <div className={`knowledge-health ${knowledgeStatus.ready ? "ready" : "not-ready"}`}>
                        <strong>
                            {knowledgeStatus.ready ? "Knowledge base ready" : "Knowledge base not ready"}
                        </strong>
                        <span>
                            {Number(knowledgeStatus.document_count || 0)} documents · {Number(knowledgeStatus.chunk_count || 0)} indexed sections
                        </span>
                        {!knowledgeStatus.ready && (
                            <small>Compliance reviews and compliance-informed drafts are unavailable until regulatory sources are indexed.</small>
                        )}
                    </div>
                )}
                
                <div className="upload-section">
                    <div className="admin-upload-dropzone">
                        <div className="admin-drop-icon">⬆</div>
                        <p>Drag files to upload</p>
                        <span>or</span>
                        <label className="admin-browse-btn">
                            Browse files
                            <input
                                type="file"
                                multiple
                                accept=".pdf,.docx,.txt"
                                onChange={handleUpload}
                            />
                        </label>
                        <small>PDF, DOCX, TXT. Max 15MB each.</small>
                    </div>

                    <div className="admin-file-list">
                        {uploadingKeys.map((name) => (
                            <div key={name} className="admin-file-row attaching">
                                <span className="admin-file-name">{name} (Uploading...)</span>
                                <div 
                                    className="admin-file-progress-bar" 
                                    style={{ width: `${uploadingFiles[name]}%` }}
                                />
                            </div>
                        ))}

                        {isLoadingDocs ? (
                            <div className="admin-spinner"></div>
                        ) : docs.length === 0 && uploadingKeys.length === 0 ? (
                            <div className="admin-status-msg info" style={{ background: 'transparent', border: 'none', color: '#94a3b8' }}>
                                No documents in the active knowledge base.
                            </div>
                        ) : (
                            docs.map((doc) => (
                                <div key={doc.filename} className="admin-file-row">
                                    <span className="admin-file-name">{doc.filename}</span>
                                    <button 
                                        className="admin-remove-file-btn" 
                                        onClick={() => handleDelete(doc.filename)}
                                        title="Delete document"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
                </section>}

                <section className="admin-section" aria-labelledby="audit-heading">
                    <div className="admin-section-heading">
                        <div>
                            <h3 id="audit-heading">Security audit trail</h3>
                            <p>Recent sign-ins, access changes and document activity.</p>
                        </div>
                    </div>
                    {auditEvents.length === 0 ? (
                        <p className="admin-empty">No security events recorded yet.</p>
                    ) : (
                        <div className="audit-list">
                            {auditEvents.slice(0, 50).map((event) => (
                                <div className="audit-row" key={event.id}>
                                    <strong>{event.action}</strong>
                                    <span>{event.targetType}{event.targetId ? ` · ${event.targetId}` : ""}</span>
                                    <time>{event.createdAt ? new Date(event.createdAt).toLocaleString() : ""}</time>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {status && (
                    <div className={`admin-status-msg ${status.type}`}>
                        {status.text}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Admin;
